import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { BoardLayout } from './useBoardLayout'

interface CollectiblesProps {
  cells: string[]
  layout: BoardLayout
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
}

export function Collectibles({ cells, layout, reducedMotion, graphics }: CollectiblesProps) {
  const items: { key: string; x: number; y: number; tile: string }[] = []
  cells.forEach((row, y) => {
    row.split('').forEach((tile, x) => {
      if (tile === 'p' || tile === 'g' || tile === 'k') items.push({ key: `${x}-${y}-${tile}`, x, y, tile })
    })
  })
  return (
    <group>
      {items.map((it) => (
        <Collectible key={it.key} info={it} layout={layout} reducedMotion={reducedMotion} graphics={graphics} />
      ))}
    </group>
  )
}

function Collectible({
  info,
  layout,
  reducedMotion,
  graphics,
}: {
  info: { x: number; y: number; tile: string }
  layout: BoardLayout
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
}) {
  const ref = useRef<THREE.Group>(null)
  const [wx, , wz] = layout.toWorld(info.x, info.y)

  useFrame((state) => {
    if (!ref.current || reducedMotion) return
    const t = state.clock.elapsedTime
    ref.current.rotation.y = t * 1.6 + info.x
    ref.current.position.y = 0.5 + Math.sin(t * 2.4 + info.x + info.y) * 0.08
  })

  return (
    <group ref={ref} position={[wx, 0.5, wz]}>
      {info.tile === 'p' && (
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <cylinderGeometry args={[0.2, 0.2, 0.06, 20]} />
          <meshStandardMaterial color="#ffcf5c" emissive="#ffb454" emissiveIntensity={0.5} metalness={0.7} roughness={0.25} />
        </mesh>
      )}
      {info.tile === 'g' && (
        <mesh castShadow>
          <octahedronGeometry args={[0.24, 0]} />
          <meshStandardMaterial color="#7de3ff" emissive="#2fb8ff" emissiveIntensity={0.6} metalness={0.4} roughness={0.1} transparent opacity={0.92} />
        </mesh>
      )}
      {info.tile === 'k' && (
        <group scale={0.9}>
          <mesh position={[0, 0.06, 0]} castShadow>
            <torusGeometry args={[0.12, 0.045, 8, 16]} />
            <meshStandardMaterial color="#ffe08a" emissive="#ffb454" emissiveIntensity={0.5} metalness={0.8} roughness={0.2} />
          </mesh>
          <mesh position={[0, -0.14, 0]}>
            <boxGeometry args={[0.06, 0.22, 0.06]} />
            <meshStandardMaterial color="#ffe08a" emissive="#ffb454" emissiveIntensity={0.4} metalness={0.8} roughness={0.2} />
          </mesh>
        </group>
      )}
      {graphics !== 'low' && (
        <pointLight
          color={info.tile === 'g' ? '#7de3ff' : '#ffcf5c'}
          distance={graphics === 'high' ? 1.6 : 1.15}
          intensity={graphics === 'high' ? 0.8 : 0.32}
        />
      )}
    </group>
  )
}
